import React from "react";
import {
  Checkbox,
  ChevronDownIcon,
  FormField,
  IconButton,
  Input,
  RadioGroup,
  SecondaryButton,
  Select,
  ShuffleIcon,
  XIcon,
} from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import { generateCouponCode } from "../utils/couponCodeUtils";

const DEFAULT_CURRENCY = "USD";

const toggleArrayValue = (arr, value) => {
  const next = new Set(arr || []);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return Array.from(next);
};

export const emptyCouponDraft = () => ({
  key: `coupon-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  code: "",
  discountType: "percent", // percent | amount | trial
  percent_off: "20",
  amount_off: "",
  currency: DEFAULT_CURRENCY,
  include_renewals: false,
  free_trial_days: "0",
  redeem_by: "",
  max_redemptions: "",
  first_time_only: false,
  plans: [],
  billing_periods: [],
  free_access_until: "",
  free_access_plan: "",
  active: true,
});

export const summarizeCouponDraft = (draft) => {
  const code = (draft?.code || "").trim() || "Untitled coupon";
  if (draft?.discountType === "trial") {
    const days = Number(draft.free_trial_days || 0);
    return `${code} · ${Number.isFinite(days) && days > 0 ? `${days}-day trial` : "trial"}`;
  }
  if (draft?.discountType === "amount") {
    const amount = draft.amount_off || "…";
    const currency = (draft.currency || DEFAULT_CURRENCY).toUpperCase();
    return `${code} · ${amount} ${currency} off`;
  }
  const pct = draft?.percent_off || "…";
  return `${code} · ${pct}% off`;
};

export const validateCouponDraft = (draft) => {
  const errors = {};
  const code = (draft.code || "").trim();
  if (!code) errors.code = "Code is required.";
  else if (code.length > 12) errors.code = "Code must be at most 12 characters.";

  const hasFreeAccessUntil = Boolean(draft.free_access_until);
  const hasFreeAccessPlan = Boolean(draft.free_access_plan);
  if (hasFreeAccessUntil !== hasFreeAccessPlan) {
    if (!hasFreeAccessUntil) {
      errors.free_access_until = "Set a free access date when a free access plan is selected.";
    }
    if (!hasFreeAccessPlan) {
      errors.free_access_plan = "Select a free access plan when a free access date is set.";
    }
  } else if (hasFreeAccessUntil) {
    const until = new Date(draft.free_access_until);
    if (Number.isNaN(until.getTime())) {
      errors.free_access_until = "Invalid date.";
    } else if (until.getTime() <= Date.now()) {
      errors.free_access_until = "Free access until must be in the future.";
    }
  }

  const trialDays = Number(draft.free_trial_days || 0);
  const hasTrial = Number.isFinite(trialDays) && trialDays > 0;

  if (draft.discountType === "trial") {
    if (!hasTrial) errors.free_trial_days = "Free trial period must be > 0 for trial-only coupons.";
  } else if (draft.discountType === "percent") {
    const v = Number(draft.percent_off);
    if (!draft.percent_off && draft.percent_off !== 0) errors.percent_off = "Percent off is required.";
    else if (Number.isNaN(v) || v <= 0 || v > 100) errors.percent_off = "Percent off must be between 0 and 100.";
  } else if (draft.discountType === "amount") {
    const v = Number(draft.amount_off);
    if (!draft.amount_off) errors.amount_off = "Amount off is required.";
    else if (Number.isNaN(v) || v <= 0) errors.amount_off = "Amount off must be > 0.";
    if (!(draft.currency || "").trim()) errors.currency = "Currency is required for amount-off coupons.";
  } else {
    errors.discountType = "Select a discount type.";
  }

  return errors;
};

export const buildCouponCreatePayload = (draft) => {
  const payload = {
    code: (draft.code || "").trim(),
    active: Boolean(draft.active),
    include_renewals: Boolean(draft.include_renewals),
    free_trial_days: Number(draft.free_trial_days || 0),
    redeem_by: draft.redeem_by ? new Date(draft.redeem_by).toISOString() : null,
    max_redemptions: draft.max_redemptions ? Number(draft.max_redemptions) : null,
    first_time_only: Boolean(draft.first_time_only),
    plans: draft.plans || [],
    billing_periods: draft.billing_periods || [],
    free_access_until: draft.free_access_until ? new Date(draft.free_access_until).toISOString() : null,
    free_access_plan: draft.free_access_plan || null,
  };

  if (draft.discountType === "percent") {
    payload.percent_off = Number(draft.percent_off);
  } else if (draft.discountType === "amount") {
    payload.amount_off = Number(draft.amount_off);
    payload.currency = (draft.currency || DEFAULT_CURRENCY).trim().toUpperCase();
  }

  return payload;
};

/**
 * Accordion editor for coupons attached at affiliate-account creation.
 * Only one draft line is expanded at a time.
 * Field set mirrors CouponsAdminPage create form (minus affiliate assignment).
 *
 * Optional `defaults` prop wires the global default-coupon templates in:
 * {
 *   regular: { configured, loading, added, code, error, onAdd, onRemove, onCodeChange },
 *   access:  { configured, loading, added, count, error, onAdd, onRemove, onCountChange },
 * }
 * Each default line can be added at most once; the parent owns the state.
 */
export default function CreateAffiliateCouponsEditor({
  drafts,
  expandedKey,
  errorsByKey = {},
  disabled = false,
  // Shown in place of the usual blurb so a disabled editor says why it is disabled.
  disabledReason = "",
  onChange,
  onExpandedKeyChange,
  defaults = null,
}) {
  const updateDraft = (key, patch) => {
    onChange((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  };

  const addDraft = () => {
    const next = emptyCouponDraft();
    onChange((prev) => [...prev, next]);
    onExpandedKeyChange(next.key);
  };

  const removeDraft = (key) => {
    onChange((prev) => prev.filter((d) => d.key !== key));
    if (expandedKey === key) onExpandedKeyChange(null);
  };

  const toggleExpanded = (key) => {
    onExpandedKeyChange(expandedKey === key ? null : key);
  };

  const regularDefault = defaults?.regular || null;
  const accessDefault = defaults?.access || null;
  const hasDefaultLines = Boolean(regularDefault?.added || accessDefault?.added);

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div>
          <div className={`${typography.label} ${textColors.primary}`}>Coupons for this affiliate</div>
          <p className={`${typography.caption} ${textColors.secondary} mt-0.5`}>
            {disabledReason ||
              "Optional. Add one or more coupons now; they will be linked to this affiliate. Edit one line at a time."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <SecondaryButton type="button" size="sm" onClick={addDraft} disabled={disabled}>
            Add coupon
          </SecondaryButton>
          {regularDefault && (
            <SecondaryButton
              type="button"
              size="sm"
              onClick={regularDefault.onAdd}
              disabled={disabled || regularDefault.added || regularDefault.loading || !regularDefault.configured}
              title={
                !regularDefault.loading && !regularDefault.configured
                  ? "No default coupon template is configured. Set one up on the Coupons page first."
                  : regularDefault.added
                    ? "The default coupon line has already been added."
                    : undefined
              }
            >
              Add default coupon
            </SecondaryButton>
          )}
          {accessDefault && (
            <SecondaryButton
              type="button"
              size="sm"
              onClick={accessDefault.onAdd}
              disabled={disabled || accessDefault.added || accessDefault.loading || !accessDefault.configured}
              title={
                !accessDefault.loading && !accessDefault.configured
                  ? "No default access-coupon template is configured. Set one up on the Coupons page first."
                  : accessDefault.added
                    ? "The default access-coupon line has already been added."
                    : undefined
              }
            >
              Add default access coupons
            </SecondaryButton>
          )}
        </div>
      </div>

      {hasDefaultLines && (
        <ul className="space-y-2">
          {regularDefault?.added && (
            <li className={`rounded-2xl border ${borders.glass} ${surfaces.glass} px-3 py-3`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <FormField
                    label="Code"
                    htmlFor="aff-default-coupon-code"
                    helperText="Default coupon from the global template. Prefilled from the username; edit to override."
                  >
                    <Input
                      id="aff-default-coupon-code"
                      value={regularDefault.code}
                      onChange={(e) => regularDefault.onCodeChange(e.target.value)}
                      error={regularDefault.error}
                      maxLength={64}
                      disabled={disabled}
                    />
                  </FormField>
                </div>
                <IconButton
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label="Remove default coupon"
                  icon={<XIcon size="sm" />}
                  onClick={regularDefault.onRemove}
                  disabled={disabled}
                />
              </div>
            </li>
          )}
          {accessDefault?.added && (
            <li className={`rounded-2xl border ${borders.glass} ${surfaces.glass} px-3 py-3`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <FormField
                    label="Count"
                    htmlFor="aff-default-access-count"
                    helperText="Number of default access coupons to create (1–20)."
                  >
                    <Input
                      id="aff-default-access-count"
                      type="number"
                      min="1"
                      max="20"
                      value={accessDefault.count}
                      onChange={(e) => accessDefault.onCountChange(e.target.value)}
                      error={accessDefault.error}
                      disabled={disabled}
                    />
                  </FormField>
                </div>
                <IconButton
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label="Remove default access coupons"
                  icon={<XIcon size="sm" />}
                  onClick={accessDefault.onRemove}
                  disabled={disabled}
                />
              </div>
            </li>
          )}
        </ul>
      )}

      {drafts.length === 0 ? (
        !hasDefaultLines && (
          <p className={`${typography.bodySmall} ${textColors.secondary}`}>No coupons yet. You can still create the account without any.</p>
        )
      ) : (
        <ul className="space-y-2">
          {drafts.map((draft, index) => {
            const isOpen = expandedKey === draft.key;
            const lineErrors = errorsByKey[draft.key] || {};
            const hasErrors = Object.keys(lineErrors).length > 0;

            return (
              <li
                key={draft.key}
                className={`rounded-2xl border ${borders.glass} ${surfaces.glass} overflow-hidden`}
              >
                <div className="flex items-stretch gap-1">
                  <button
                    type="button"
                    className={`flex-1 flex items-center gap-2 px-3 py-2.5 text-left ${
                      hasErrors ? "text-red-700 dark:text-red-300" : textColors.primary
                    } hover:bg-black/[0.03] dark:hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400`}
                    onClick={() => toggleExpanded(draft.key)}
                    aria-expanded={isOpen}
                    disabled={disabled}
                  >
                    <ChevronDownIcon
                      size="sm"
                      className={`shrink-0 transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`}
                      aria-hidden="true"
                    />
                    <span className={`${typography.bodySmall} font-medium truncate`}>
                      {summarizeCouponDraft(draft)}
                    </span>
                    <span className={`${typography.caption} ${textColors.secondary} shrink-0`}>
                      #{index + 1}
                    </span>
                  </button>
                  <div className="flex items-center pr-1">
                    <IconButton
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Remove coupon ${index + 1}`}
                      icon={<XIcon size="sm" />}
                      onClick={() => removeDraft(draft.key)}
                      disabled={disabled}
                    />
                  </div>
                </div>

                {isOpen && (
                  <div className={`border-t ${borders.glass} px-3 py-3 space-y-3`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FormField label="Code" htmlFor={`aff-coupon-code-${draft.key}`} required>
                        <Input
                          id={`aff-coupon-code-${draft.key}`}
                          value={draft.code}
                          onChange={(e) => updateDraft(draft.key, { code: e.target.value })}
                          error={lineErrors.code}
                          placeholder="e.g. GROWTH20"
                          maxLength={12}
                          disabled={disabled}
                          rightAdornment={
                            <IconButton
                              type="button"
                              size="sm"
                              variant="ghost"
                              aria-label="Generate random coupon code"
                              title="Generate random coupon code"
                              icon={<ShuffleIcon size="sm" />}
                              disabled={disabled}
                              onClick={() => updateDraft(draft.key, { code: generateCouponCode(12) })}
                            />
                          }
                        />
                      </FormField>

                      <FormField
                        label="Redeem by"
                        htmlFor={`aff-coupon-redeem-${draft.key}`}
                        optional
                        helperText="Optional. Must be in the future."
                      >
                        <Input
                          id={`aff-coupon-redeem-${draft.key}`}
                          type="datetime-local"
                          value={draft.redeem_by}
                          onChange={(e) => updateDraft(draft.key, { redeem_by: e.target.value })}
                          disabled={disabled}
                        />
                      </FormField>

                      <FormField
                        label="Max redemptions"
                        htmlFor={`aff-coupon-max-${draft.key}`}
                        optional
                      >
                        <Input
                          id={`aff-coupon-max-${draft.key}`}
                          type="number"
                          min="1"
                          value={draft.max_redemptions}
                          onChange={(e) => updateDraft(draft.key, { max_redemptions: e.target.value })}
                          placeholder="e.g. 100"
                          disabled={disabled}
                        />
                      </FormField>

                      <FormField
                        label="Free trial period (days)"
                        htmlFor={`aff-coupon-trial-${draft.key}`}
                        helperText="Optional. Applies only on free → paid checkout. Delays the first invoice by this many days."
                      >
                        <Input
                          id={`aff-coupon-trial-${draft.key}`}
                          type="number"
                          min="0"
                          value={draft.free_trial_days}
                          onChange={(e) => updateDraft(draft.key, { free_trial_days: e.target.value })}
                          error={lineErrors.free_trial_days}
                          disabled={disabled}
                        />
                      </FormField>

                      <FormField
                        label="Free access until"
                        htmlFor={`aff-coupon-free-until-${draft.key}`}
                        optional
                        helperText="Optional. Grants free access to a plan until this date. Must be set together with a free access plan."
                      >
                        <Input
                          id={`aff-coupon-free-until-${draft.key}`}
                          type="datetime-local"
                          value={draft.free_access_until}
                          onChange={(e) => updateDraft(draft.key, { free_access_until: e.target.value })}
                          error={lineErrors.free_access_until}
                          disabled={disabled}
                        />
                      </FormField>

                      <FormField
                        label="Free access plan"
                        htmlFor={`aff-coupon-free-plan-${draft.key}`}
                        optional
                        helperText="Optional. Plan granted for free until the date above. Must be set together with a free access date."
                      >
                        <Select
                          id={`aff-coupon-free-plan-${draft.key}`}
                          value={draft.free_access_plan}
                          onChange={(e) => updateDraft(draft.key, { free_access_plan: e.target.value })}
                          options={[
                            { value: "", label: "None" },
                            { value: "plus", label: "Plus" },
                            { value: "pro", label: "Pro" },
                          ]}
                          placeholder={null}
                          error={lineErrors.free_access_plan}
                          disabled={disabled}
                        />
                      </FormField>
                    </div>

                    <FormField label="Discount type" htmlFor={`aff-coupon-dtype-${draft.key}`}>
                      <RadioGroup
                        name={`aff-coupon-dtype-${draft.key}`}
                        value={draft.discountType}
                        onChange={(value) => updateDraft(draft.key, { discountType: value })}
                        options={[
                          { value: "percent", label: "Percent off" },
                          { value: "amount", label: "Amount off" },
                          { value: "trial", label: "Free trial only" },
                        ]}
                        error={lineErrors.discountType}
                        orientation="horizontal"
                        aria-label="Discount type"
                        disabled={disabled}
                      />
                    </FormField>

                    {draft.discountType === "trial" ? (
                      <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                        This coupon provides a free trial period (no price discount).
                      </p>
                    ) : null}

                    {draft.discountType === "percent" && (
                      <FormField label="Percent off" htmlFor={`aff-coupon-pct-${draft.key}`} required>
                        <Input
                          id={`aff-coupon-pct-${draft.key}`}
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={draft.percent_off}
                          onChange={(e) => updateDraft(draft.key, { percent_off: e.target.value })}
                          error={lineErrors.percent_off}
                          placeholder="e.g. 10"
                          disabled={disabled}
                        />
                      </FormField>
                    )}

                    {draft.discountType === "amount" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <FormField label="Amount off" htmlFor={`aff-coupon-amt-${draft.key}`} required>
                          <Input
                            id={`aff-coupon-amt-${draft.key}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={draft.amount_off}
                            onChange={(e) => updateDraft(draft.key, { amount_off: e.target.value })}
                            error={lineErrors.amount_off}
                            placeholder="e.g. 5.00"
                            disabled={disabled}
                          />
                        </FormField>
                        <FormField label="Currency" htmlFor={`aff-coupon-cur-${draft.key}`} required>
                          <Input
                            id={`aff-coupon-cur-${draft.key}`}
                            value={draft.currency}
                            onChange={(e) => updateDraft(draft.key, { currency: e.target.value })}
                            error={lineErrors.currency}
                            disabled={disabled}
                            maxLength={3}
                            placeholder="USD"
                          />
                        </FormField>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className={`rounded-xl border ${borders.glass} p-3 space-y-2`}>
                        <p className={`${typography.label} ${textColors.secondary}`}>Options</p>
                        <Checkbox
                          checked={Boolean(draft.active)}
                          onChange={(e) => updateDraft(draft.key, { active: e.target.checked })}
                          label="Active (initial)"
                          disabled={disabled}
                        />
                        <Checkbox
                          checked={Boolean(draft.include_renewals)}
                          onChange={(e) =>
                            updateDraft(draft.key, { include_renewals: e.target.checked })
                          }
                          label="Include renewals"
                          disabled={disabled || draft.discountType === "trial"}
                        />
                        <Checkbox
                          checked={Boolean(draft.first_time_only)}
                          onChange={(e) =>
                            updateDraft(draft.key, { first_time_only: e.target.checked })
                          }
                          label="First-time only"
                          disabled={disabled}
                        />
                      </div>

                      <div className={`rounded-xl border ${borders.glass} p-3 space-y-2`}>
                        <p className={`${typography.label} ${textColors.secondary}`}>Plans (optional)</p>
                        <Checkbox
                          checked={(draft.plans || []).includes("free")}
                          onChange={() =>
                            updateDraft(draft.key, { plans: toggleArrayValue(draft.plans, "free") })
                          }
                          label="Free"
                          disabled={disabled}
                        />
                        <Checkbox
                          checked={(draft.plans || []).includes("plus")}
                          onChange={() =>
                            updateDraft(draft.key, { plans: toggleArrayValue(draft.plans, "plus") })
                          }
                          label="Plus"
                          disabled={disabled}
                        />
                        <Checkbox
                          checked={(draft.plans || []).includes("pro")}
                          onChange={() =>
                            updateDraft(draft.key, { plans: toggleArrayValue(draft.plans, "pro") })
                          }
                          label="Pro"
                          disabled={disabled}
                        />
                        <p className={`${typography.caption} ${textColors.muted}`}>Leave empty for all plans.</p>
                      </div>

                      <div className={`rounded-xl border ${borders.glass} p-3 space-y-2`}>
                        <p className={`${typography.label} ${textColors.secondary}`}>Billing periods (optional)</p>
                        <Checkbox
                          checked={(draft.billing_periods || []).includes("monthly")}
                          onChange={() =>
                            updateDraft(draft.key, {
                              billing_periods: toggleArrayValue(draft.billing_periods, "monthly"),
                            })
                          }
                          label="Monthly"
                          disabled={disabled}
                        />
                        <Checkbox
                          checked={(draft.billing_periods || []).includes("yearly")}
                          onChange={() =>
                            updateDraft(draft.key, {
                              billing_periods: toggleArrayValue(draft.billing_periods, "yearly"),
                            })
                          }
                          label="Yearly"
                          disabled={disabled}
                        />
                        <p className={`${typography.caption} ${textColors.muted}`}>
                          Leave empty for all billing periods.
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <SecondaryButton type="button" size="sm" onClick={() => onExpandedKeyChange(null)}>
                        Done
                      </SecondaryButton>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
